import Download from '@mui/icons-material/Download'
import { Box, Button, Stack, Tooltip } from '@mui/material'
import { useCallback, useContext, useEffect, useState } from 'react'
import AppContext from '../../../context/appContext'
import { useFormDefinition } from '../../../hooks/data/useFormDefinitions'
import { useCreateSOCSample, useUpdateSOCSample } from '../../../hooks/data/useSOCSamples'
import {
  type Farm,
  type FieldDefinition,
  type FormDefinition,
  type MonitoringSOCSamplingAreaSample,
  type MonitoringSOCSiteSample,
  type SOCSampleWithDocuments,
  type SOCSamplesLoadResponse,
} from '../../../types/apiModels'
import { useProcessLoading } from '../../../utils/Loading/useProcessLoading'
import { PROGRAM_IDS, monitoringWorkflowsTypes } from '../../../utils/constants'
import DataGrid from '../DataGrid/DataGrid'
import { useDownloadSOCSample } from '../export/useDownloadSOCSample'
import FormDefinitionDialog from '../FormDefinitionDialog'
import LoadDataButton from './LoadDataButton'
import { useSOCSamplingAreasColumns } from './useSOCSamplingAreasColumns'
import { useSOCSitesColumns } from './useSOCSitesColumns'

type GridRow = {
  id: string | number
  isNew?: boolean
  errors?: Record<string, string>
  [key: string]: unknown
}
type GridColumn = {
  field: string
  validate?: (args: { value: unknown; row: GridRow; column: GridColumn }) => string | null
  [key: string]: unknown
}

export type SOCSampleDialogMode = 'view' | 'edit'

const SOCSampleDialog: React.FC<{
  farm: Farm
  socSample: SOCSampleWithDocuments | null
  open: boolean
  handleClose: () => void
  readOnly: boolean
  initialMode?: SOCSampleDialogMode
}> = ({ farm, socSample, open, handleClose, readOnly, initialMode = 'edit' }) => {
  const { processLoading } = useProcessLoading()
  const [isEditingMode, setIsEditingMode] = useState(false)
  const [mode, setMode] = useState<SOCSampleDialogMode>(initialMode)
  const isReadOnly = readOnly || mode === 'view'
  const { mutate: downloadSOCSample, isPending: isDownloading } = useDownloadSOCSample(farm)
  const { programConfig, partialChanges, setPartialChanges, setConfirmationModalConfig } =
    useContext(AppContext)
  const [state, setState] = useState<{
    formData: Record<string, unknown> | null
    formDefinition: FormDefinition | null
    fieldInputsFromFormDefinition: FieldDefinition[] | null
  }>({
    formData: null,
    formDefinition: null,
    fieldInputsFromFormDefinition: null,
  })
  // Defaults to true so new samples bypass the completeness guard; the guard only applies
  // to samples explicitly marked incomplete (which requires the FormCompleteStatus UI, pending re-enable).
  const [isCompleted, setIsCompleted] = useState(true)
  const [uncompletedReason, setUncompletedReason] = useState('')

  const { mutateAsync: createSOCSample } = useCreateSOCSample(farm.id)
  const { mutateAsync: updateSOCSample } = useUpdateSOCSample(farm.id)

  const socSites = useSOCSitesColumns(farm.id)
  const socSamplingAreas = useSOCSamplingAreasColumns(farm.id)
  // The sites grid has no optional columns of its own; this state exists solely to satisfy DataGrid's required prop.
  const [sitesOptionalColumns, setSitesOptionalColumns] = useState<Record<string, boolean>>({})

  const socSampleFormDefinitionId = programConfig?.formDefinitions?.monitoringSOCSample?.id
  const { data: monitoringSOCSampleFD } = useFormDefinition(socSampleFormDefinitionId)

  // The definition now arrives from React Query; this effect only sequences the dependent
  // side-effects (column loading before row seeding) once the definition is available. The
  // `loadColumns → getInitialSiteRows/setSamples` order is load-bearing: the ReTerra initial
  // row seed (card 5123) needs the site columns resolved before it can build the depth rows.
  useEffect(() => {
    if (!open || !monitoringSOCSampleFD) return

    async function seedColumnsAndRows() {
      await processLoading({
        loadingMessage: 'Cargando información de monitoreo suelos...',
        successfulMessage: '',
        doAction: async () => {
          const newFieldInputsFromFormDefinition = monitoringSOCSampleFD.fields.filter(
            field => field.component_type === 'FileInput',
          )

          let formDataFromDocumentsEntity: Record<string, unknown> = {}
          if (socSample) {
            formDataFromDocumentsEntity = { ...socSample }

            if (socSample.documents) {
              newFieldInputsFromFormDefinition.forEach(fieldInputFromFormDefinition => {
                const documentFromEntityInFieldDefinition = socSample.documents
                  .filter(documentFromEntity => {
                    return (
                      fieldInputFromFormDefinition.fileType === documentFromEntity.documentTypeId
                    )
                  })
                  .map(documentFromEntity => documentFromEntity.url)
                formDataFromDocumentsEntity[fieldInputFromFormDefinition.name] =
                  documentFromEntityInFieldDefinition
              })
            }

            setIsCompleted(socSample.isCompleted ?? false)
            setUncompletedReason(socSample.uncompletedReason ?? '')
          }

          await socSites.loadColumns()
          // getInitialSiteRows itself decides what to seed: ReTerra always seeds (falling back to
          // one empty depth row), mixed SOC-V2 programs seed only the double-depth rows found (or
          // none), and pure scalar programs return []. See useSOCSitesColumns.getInitialSiteRows.
          const siteSamples = socSample?.monitoringSOCSitesSamples ?? socSites.getInitialSiteRows()
          socSites.setSamples(siteSamples)

          const samplingAreaColumns = await socSamplingAreas.loadColumns()
          const samplingAreaSamples = socSample?.monitoringSOCSamplingAreaSamples || []
          socSamplingAreas.setSamples(samplingAreaSamples)

          socSamplingAreas.initializeOptionalColumns(samplingAreaSamples, samplingAreaColumns)

          setState({
            formData: formDataFromDocumentsEntity,
            formDefinition: monitoringSOCSampleFD,
            fieldInputsFromFormDefinition: newFieldInputsFromFormDefinition,
          })
        },
      })
    }

    seedColumnsAndRows()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, monitoringSOCSampleFD, socSample, programConfig, farm.id, processLoading])

  const { formData, formDefinition, fieldInputsFromFormDefinition } = state
  // The export reads the saved sample, so unsaved edits would silently be left out of the file.
  const hasUnsavedChanges = !isReadOnly && (partialChanges || isEditingMode)

  const handlePartialChangesClose = () => {
    handleClose()
    setPartialChanges(false)
  }

  const validateRows = ({
    rows,
    columns,
    optionalColumns = {},
  }: {
    rows: GridRow[]
    columns: GridColumn[]
    optionalColumns?: Record<string, boolean>
  }) => {
    const validatedRows = rows.map(row => {
      const errors: Record<string, string> = {}
      columns
        .filter(column => column.validate && !optionalColumns[column.field])
        .forEach(column => {
          const { field } = column
          const error = column.validate?.({ value: row[field], row, column })
          if (error) errors[field] = error
        })
      return { ...row, errors }
    })
    const hasRowErrors = validatedRows.some(row => Object.keys(row.errors ?? {}).length > 0)
    return { isValid: !hasRowErrors, validatedRows }
  }

  const handleProcessRowUpdateSamplingAreas = (newRow: GridRow) => {
    setPartialChanges(true)
    return socSamplingAreas.processRowUpdate(newRow)
  }

  const handleProcessRowUpdateSites = (newRow: GridRow) => {
    setPartialChanges(true)
    return socSites.processRowUpdate(newRow)
  }

  const handleSaveFormData = async (data: Record<string, unknown>) => {
    if (isReadOnly) return

    const isReTerra = programConfig?.programId === PROGRAM_IDS.RETERRA

    const { isValid: isSitesValid, validatedRows: validatedMonitoringSOCSitesSamples } =
      validateRows({
        rows: socSites.samples,
        columns: socSites.columns,
        optionalColumns: sitesOptionalColumns,
      })

    const {
      isValid: isSamplingAreasValid,
      validatedRows: validatedMonitoringSOCSamplingAreaSamples,
    } = validateRows({
      rows: socSamplingAreas.samples,
      columns: socSamplingAreas.columns,
      optionalColumns: socSamplingAreas.optionalColumns,
    })

    if (!isCompleted && !uncompletedReason.trim()) {
      return
    }

    // Double-depth guards (ReTerra + SOC-V2): surface per-row errors using the same mechanism as
    // the generic column validators so the user gets visible feedback instead of a silent no-op.
    // Depth support is per-activity, not per-program: a SARA/"Sin programa" sample can mix scalar
    // SOC/POA rows with SOC-V2 double-depth rows, so only rows tied to a double-depth activity
    // (or already carrying seeded depth values) are subject to these checks — scalar rows keep
    // their existing single-row/no-depth behavior via the generic validateRows check below.
    const doubleDepthActivityIds = new Set(
      socSites.completedSOCActivities
        .filter(
          activity =>
            typeof activity.monitoringWorkflowId === 'number' &&
            (activity.monitoringWorkflowId === monitoringWorkflowsTypes.SOCRETERRA ||
              activity.monitoringWorkflowId === monitoringWorkflowsTypes.SOCV2),
        )
        .map(activity => activity.id),
    )
    const isDepthRow = (row: GridRow) =>
      isReTerra ||
      (typeof row.monitoringSOCActivityId === 'string' &&
        doubleDepthActivityIds.has(row.monitoringSOCActivityId)) ||
      row.initialDepth != null ||
      row.finalDepth != null

    if (isReTerra || doubleDepthActivityIds.size > 0) {
      const depthActiveRows = socSites.samples.filter(row => !row.isDeleted && isDepthRow(row))

      const depthKeys = depthActiveRows.map(
        row =>
          `${row.monitoringSOCActivityId ?? 'null'}|${row.initialDepth ?? 'null'}|${row.finalDepth ?? 'null'}`,
      )
      const hasDuplicate = depthKeys.some((key, idx) => depthKeys.indexOf(key) !== idx)

      const hasUnfilledDepthRow =
        isCompleted &&
        depthActiveRows.some(
          row => row.dap == null || row.sampleVolume == null || row.dryWeight == null,
        )

      if (hasDuplicate || hasUnfilledDepthRow) {
        const rowsWithErrors = (validatedMonitoringSOCSitesSamples as GridRow[]).map(row => {
          if (row.isDeleted || !isDepthRow(row)) return row
          const rowKey = `${row.monitoringSOCActivityId ?? 'null'}|${row.initialDepth ?? 'null'}|${row.finalDepth ?? 'null'}`
          const isDup = hasDuplicate && depthKeys.filter(k => k === rowKey).length > 1
          const isUnfilled =
            hasUnfilledDepthRow &&
            (row.dap == null || row.sampleVolume == null || row.dryWeight == null)
          if (!isDup && !isUnfilled) return row
          return {
            ...row,
            errors: {
              ...row.errors,
              ...(isDup
                ? {
                    monitoringSOCActivityId: 'Actividad y profundidad duplicadas en el mismo sitio',
                  }
                : {}),
              ...(isUnfilled ? { dap: 'Todas las profundidades deben estar completadas' } : {}),
            },
          }
        })
        // Rows are augmented with an `errors` field at runtime; the concrete API types don't carry it.
        socSites.setSamples(rowsWithErrors as unknown as MonitoringSOCSiteSample[])
        return
      }
    }

    if (!isSitesValid || !isSamplingAreasValid) {
      // Rows are augmented with an `errors` field at runtime; the concrete API types don't carry it.
      socSites.setSamples(
        validatedMonitoringSOCSitesSamples as unknown as MonitoringSOCSiteSample[],
      )
      socSamplingAreas.setSamples(
        validatedMonitoringSOCSamplingAreaSamples as unknown as MonitoringSOCSamplingAreaSample[],
      )
      return
    }

    const convertRowToEntity = ({ rows }: { rows: GridRow[] }) => {
      return rows.map(row => {
        // Strip grid-only metadata (`isNew`, `errors`) before persisting.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { isNew, errors: _errors, id, ...rest } = row
        return isNew ? rest : { ...rest, id }
      })
    }

    if (isEditingMode) {
      return
    }

    await processLoading({
      loadingMessage: 'Guardando resultados...',
      successfulMessage: 'Resultados guardados',
      doAction: async () => {
        const socSampleToSave = {
          ...data,
          farmId: farm.id,
          isCompleted,
          uncompletedReason,
          monitoringSOCSitesSamples: convertRowToEntity({
            rows: validatedMonitoringSOCSitesSamples,
          }),
          monitoringSOCSamplingAreaSamples: convertRowToEntity({
            rows: validatedMonitoringSOCSamplingAreaSamples,
          }),
        }

        if (!socSample?.id) {
          await createSOCSample({ socSample: socSampleToSave as Partial<SOCSampleWithDocuments> })
        } else {
          await updateSOCSample({
            socSample: {
              id: socSample.id,
              ...socSampleToSave,
            } as Partial<SOCSampleWithDocuments> & {
              id: string
            },
          })
        }

        handlePartialChangesClose()
      },
    })
  }

  const handleCloseAndReset = () => {
    if (partialChanges && !isReadOnly) {
      setConfirmationModalConfig({
        open: true,
        confirmAction: () => {
          setState(s => ({ ...s, formData: null }))
          setIsCompleted(true)
          setUncompletedReason('')
          handlePartialChangesClose()
        },
      })
    } else {
      setState(s => ({ ...s, formData: null }))
      setIsCompleted(true)
      setUncompletedReason('')
      handleClose()
    }
  }

  const handleDataLoaded = useCallback(
    (data: SOCSamplesLoadResponse) => {
      if (!data?.socSamples) {
        return
      }

      const { monitoringSOCSiteSamples, monitoringSOCSamplingAreaSamples } = data.socSamples

      if (monitoringSOCSiteSamples && Array.isArray(monitoringSOCSiteSamples)) {
        const mappedSiteSamples = monitoringSOCSiteSamples.map((sample, index) => ({
          id: `loaded-site-${Date.now()}-${index}`,
          isNew: true,
          ...sample,
        }))
        socSites.setSamples(prev => [...prev, ...mappedSiteSamples])
      }

      if (monitoringSOCSamplingAreaSamples && Array.isArray(monitoringSOCSamplingAreaSamples)) {
        const mappedSamplingAreaSamples = monitoringSOCSamplingAreaSamples.map((sample, index) => ({
          id: `loaded-area-${Date.now()}-${index}`,
          isNew: true,
          ...sample,
        }))
        socSamplingAreas.setSamples(prev => [...prev, ...mappedSamplingAreaSamples])
      }

      setPartialChanges(true)
    },
    [socSites, socSamplingAreas, setPartialChanges],
  )

  return (
    formData &&
    formDefinition?.fields?.length > 0 &&
    fieldInputsFromFormDefinition && (
      <FormDefinitionDialog
        data={formData}
        formDefinition={formDefinition}
        handleClose={handleCloseAndReset}
        isEditingMode={isEditingMode}
        open={open}
        readOnly={isReadOnly}
        saveFormData={handleSaveFormData}
        onEdit={readOnly ? undefined : () => setMode('edit')}
      >
        <Stack direction="column" sx={{ alignItems: 'center', justifyContent: 'center' }}>
          {socSample?.id && (
            <Tooltip title={hasUnsavedChanges ? 'Guardá los cambios para descargar' : ''}>
              <Box sx={{ alignSelf: 'flex-end' }}>
                <Button
                  disabled={isDownloading || hasUnsavedChanges}
                  startIcon={<Download />}
                  variant="outlined"
                  onClick={() => downloadSOCSample(socSample)}
                >
                  Descargar
                </Button>
              </Box>
            </Tooltip>
          )}
          {!isReadOnly && (
            <LoadDataButton
              farm={farm}
              hasCompletedSOCActivities={socSites.completedSOCActivities.length > 0}
              readOnly={isReadOnly}
              onDataLoaded={handleDataLoaded}
            />
          )}
          <DataGrid
            key="soc-sites-grid"
            hideFooter
            addButtonCaption="Agregar sitio"
            columns={socSites.columns}
            optionalColumns={sitesOptionalColumns}
            processRowUpdate={handleProcessRowUpdateSites}
            readOnly={isReadOnly}
            rows={socSites.samples}
            setIsEditingMode={setIsEditingMode}
            setOptionalColumns={setSitesOptionalColumns}
            setRows={socSites.setSamples}
            subtitle="Para poder guardar el informe es necesario que al agregar un sitio complete todos los campos de la fila correspondiente."
            title="Actividades SOC completas por sitio de monitoreo"
          />
          <DataGrid
            key="soc-sampling-grid"
            hideFooter
            addButtonCaption="Agregar estrato"
            columns={socSamplingAreas.columns}
            optionalColumns={socSamplingAreas.optionalColumns}
            processRowUpdate={handleProcessRowUpdateSamplingAreas}
            readOnly={isReadOnly}
            rows={socSamplingAreas.samples}
            setIsEditingMode={setIsEditingMode}
            setOptionalColumns={socSamplingAreas.setOptionalColumns}
            setRows={socSamplingAreas.setSamples}
            subtitle="Para poder guardar el informe es necesario que al agregar un estrato complete todos los campos de la fila correspondiente."
            title="Actividades SOC completas compuestas por estrato"
          />
        </Stack>
      </FormDefinitionDialog>
    )
  )
}

export default SOCSampleDialog
